import { useEffect, useState } from "react"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { api, apiError } from "@/services/api"
import type { OrgUnit } from "@/types"

interface OrgUnitFormDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** null = Neuanlage einer Untereinheit unter parentId; sonst Umbenennen */
  mode: "create" | "rename"
  /** Bei "create": id der Übereinheit (null = neue Wurzel). Bei "rename": id der Einheit selbst. */
  targetUnit: { id: string | null; name: string } | null
  onDone: (unit: OrgUnit) => void
}

/** Formular-Dialog fuer "Untereinheit anlegen" und "Umbenennen" im Organigramm. */
export function OrgUnitFormDialog({
  open,
  onOpenChange,
  mode,
  targetUnit,
  onDone,
}: OrgUnitFormDialogProps) {
  const queryClient = useQueryClient()
  const [name, setName] = useState("")

  useEffect(() => {
    if (!open) return
    setName(mode === "rename" ? (targetUnit?.name ?? "") : "")
  }, [open, mode, targetUnit])

  const mutation = useMutation({
    mutationFn: () =>
      mode === "create"
        ? api.organization.orgUnits.create({
            name: name.trim(),
            parentId: targetUnit?.id ? Number(targetUnit.id) : null,
          })
        : api.organization.orgUnits.update(targetUnit!.id!, { name: name.trim() }),
    onSuccess: (unit) => {
      queryClient.invalidateQueries({ queryKey: ["org-chart"] })
      queryClient.invalidateQueries({ queryKey: ["org-units"] })
      toast.success(mode === "create" ? "Untereinheit angelegt" : "Einheit umbenannt")
      onOpenChange(false)
      onDone(unit)
    },
    onError: (e) => toast.error(apiError(e)),
  })

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>
            {mode === "create" ? "Untereinheit anlegen" : "Einheit umbenennen"}
          </DialogTitle>
          <DialogDescription>
            {mode === "create"
              ? `Neue Untereinheit unter „${targetUnit?.name ?? "Organigramm"}“.`
              : `Neuer Name für „${targetUnit?.name}“.`}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5 py-2">
          <Label htmlFor="org-unit-name">Name</Label>
          <Input
            id="org-unit-name"
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && name.trim() !== "") mutation.mutate()
            }}
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Abbrechen
          </Button>
          <Button disabled={name.trim() === "" || mutation.isPending} onClick={() => mutation.mutate()}>
            Speichern
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
